import React, { useCallback, useEffect, useRef, useState } from "react";
import { Download, ExternalLink, FileWarning, Loader2, ZoomIn, ZoomOut } from "lucide-react";
import * as pdfjs from "pdfjs-dist/legacy/build/pdf.mjs";
import { Button } from "../ui/button";
import { fetchPolicyPdfBlob } from "../../lib/policies";
import { downloadBlob, errMsg } from "../../lib/api";

// pdf.js worker is emitted by webpack as its own asset (same version as the API — no CDN, works offline)
pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/legacy/build/pdf.worker.min.mjs", import.meta.url).toString();

const ZOOMS = [0.75, 1, 1.25, 1.5, 2];
const END_TOLERANCE_PX = 12;

/**
 * Phase 218 — inline PDF viewer for a policy. The file is fetched with the auth token and every page is drawn with
 * pdf.js on a canvas, so it also works on phones (Android Chrome / iOS Safari do not show PDFs inside an iframe).
 * If pdf.js cannot open the file the browser's own viewer (iframe) is used; "Open in new tab" / "Download" always work.
 *
 * Read-through check: pass `onReadProgress` to be told how far the reader has scrolled —
 *   { percent, pagesRead, pages, reachedEnd, tracked }
 * `reachedEnd` turns true (and stays true) once the bottom of the last page has been scrolled into view, or at once
 * when the whole document fits without scrolling. When the document cannot be tracked (browser viewer fallback or a
 * load error) `tracked` is false and `reachedEnd` is true so nobody is locked out.
 */
export function PolicyPdfViewer({ code, policy, version, className = "", height = "h-[55vh] min-h-[320px]", testId = "policy-pdf-viewer", onReadProgress }) {
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  const [doc, setDoc] = useState(null);
  const [fallback, setFallback] = useState(false);
  const [zoomIdx, setZoomIdx] = useState(1);
  const [width, setWidth] = useState(0);
  const containerRef = useRef(null);
  const renderedRef = useRef(new Set());   // page numbers drawn for the current width / zoom
  const progressRef = useRef({ percent: 0, pagesRead: 0, reachedEnd: false });
  const onProgressRef = useRef(onReadProgress);
  onProgressRef.current = onReadProgress;
  const v = version || policy?.version;

  const report = useCallback((next) => {
    const prev = progressRef.current;
    const merged = { ...prev, ...next, reachedEnd: prev.reachedEnd || !!next.reachedEnd };   // sticky once reached
    if (merged.percent === prev.percent && merged.pagesRead === prev.pagesRead && merged.reachedEnd === prev.reachedEnd && merged.tracked === prev.tracked) return;
    progressRef.current = merged;
    onProgressRef.current?.(merged);
  }, []);

  // 1) fetch the PDF bytes once per policy / version
  useEffect(() => {
    let alive = true;
    let created = null;
    setBlob(null); setUrl(null); setError(null); setDoc(null); setFallback(false);
    renderedRef.current = new Set();
    progressRef.current = { percent: 0, pagesRead: 0, reachedEnd: false };
    if (!code || !policy?.id) return undefined;
    fetchPolicyPdfBlob(code, policy.id, version)
      .then((b) => {
        if (!alive) return;
        created = URL.createObjectURL(b);
        setBlob(b); setUrl(created);
      })
      .catch((e) => { if (alive) { setError(errMsg(e)); report({ percent: 100, reachedEnd: true, tracked: false }); } });
    return () => { alive = false; if (created) URL.revokeObjectURL(created); };
  }, [code, policy?.id, policy?.file_id, version, report]);

  // 2) open it with pdf.js (fallback to the browser viewer when that fails)
  useEffect(() => {
    if (!blob) return undefined;
    let alive = true;
    let task = null;
    blob.arrayBuffer()
      .then((buf) => { task = pdfjs.getDocument({ data: new Uint8Array(buf) }); return task.promise; })
      .then((d) => { if (alive) { setDoc(d); report({ pages: d.numPages, tracked: true }); } else d.destroy(); })
      .catch(() => { if (alive) { setFallback(true); report({ percent: 100, reachedEnd: true, tracked: false }); } });
    return () => { alive = false; try { task?.destroy?.(); } catch { /* ignore */ } };
  }, [blob, report]);
  useEffect(() => () => { try { doc?.destroy?.(); } catch { /* ignore */ } }, [doc]);

  // 3) fit pages to the container width (re-measured on resize / rotation)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const measure = () => setWidth(Math.max(0, el.clientWidth - 24));
    measure();
    if (typeof ResizeObserver === "undefined") { window.addEventListener("resize", measure); return () => window.removeEventListener("resize", measure); }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [doc, fallback, error]);
  useEffect(() => { renderedRef.current = new Set(); }, [width, zoomIdx]);

  // 4) reading progress: how far down the document the reader is (and whether the end has been reached)
  const evaluate = useCallback(() => {
    const el = containerRef.current;
    if (!el || !doc) return;
    const allRendered = renderedRef.current.size >= doc.numPages;
    const bottom = el.scrollTop + el.clientHeight;
    const total = el.scrollHeight || 1;
    const percent = Math.max(0, Math.min(100, Math.round((bottom / total) * 100)));
    // pages whose top edge has scrolled into view
    let pagesRead = 0;
    const pages = el.querySelectorAll("[data-pdf-page]");
    const top = el.getBoundingClientRect().top;
    pages.forEach((pg) => { if (pg.getBoundingClientRect().top - top < el.clientHeight - 8) pagesRead += 1; });
    const fits = total <= el.clientHeight + END_TOLERANCE_PX;
    const atBottom = bottom >= total - END_TOLERANCE_PX;
    report({ percent, pagesRead: Math.min(doc.numPages, pagesRead), pages: doc.numPages, tracked: true,
      reachedEnd: allRendered && (fits || atBottom) });
  }, [doc, report]);
  const onPageRendered = useCallback((num) => { renderedRef.current.add(num); evaluate(); }, [evaluate]);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !doc) return undefined;
    const h = () => evaluate();
    el.addEventListener("scroll", h, { passive: true });
    const t = setTimeout(evaluate, 50);
    return () => { el.removeEventListener("scroll", h); clearTimeout(t); };
  }, [doc, width, zoomIdx, evaluate]);

  const open = () => { if (url) window.open(url, "_blank", "noopener"); };
  const download = () => downloadBlob(`/t/${code}/policies/${policy.id}/file?download=1${version ? `&version=${version}` : ""}`, policy.filename || "policy.pdf").catch((e) => setError(errMsg(e)));
  const zoom = ZOOMS[zoomIdx];

  return (
    <div className={`rounded-lg border bg-muted/30 overflow-hidden ${className}`} data-testid={testId}>
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-card text-xs">
        <span className="truncate text-muted-foreground" data-testid={`${testId}-filename`}>
          {policy?.filename || "policy.pdf"}{v ? ` • v${v}` : ""}{policy?.size ? ` • ${(policy.size / 1024).toFixed(0)} KB` : ""}{doc ? ` • ${doc.numPages} page${doc.numPages === 1 ? "" : "s"}` : ""}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {doc && !fallback && (
            <>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => setZoomIdx((i) => Math.max(0, i - 1))} disabled={zoomIdx === 0} aria-label="Zoom out" data-testid={`${testId}-zoom-out`}><ZoomOut className="h-3.5 w-3.5" /></Button>
              <span className="w-10 text-center tabular-nums text-muted-foreground" data-testid={`${testId}-zoom`}>{Math.round(zoom * 100)}%</span>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => setZoomIdx((i) => Math.min(ZOOMS.length - 1, i + 1))} disabled={zoomIdx === ZOOMS.length - 1} aria-label="Zoom in" data-testid={`${testId}-zoom-in`}><ZoomIn className="h-3.5 w-3.5" /></Button>
            </>
          )}
          <Button type="button" variant="ghost" size="sm" className="h-8" onClick={open} disabled={!url} data-testid={`${testId}-open`}>
            <ExternalLink className="h-3.5 w-3.5 mr-1" /> <span className="hidden sm:inline">Open in new tab</span><span className="sm:hidden">Open</span>
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-8" onClick={download} data-testid={`${testId}-download`}>
            <Download className="h-3.5 w-3.5 mr-1" /> Download
          </Button>
        </div>
      </div>
      {error ? (
        <div className={`flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground ${height}`} data-testid={`${testId}-error`}>
          <FileWarning className="h-6 w-6" /> {error}
        </div>
      ) : !url || (!doc && !fallback) ? (
        <div className={`flex items-center justify-center gap-2 text-sm text-muted-foreground ${height}`} data-testid={`${testId}-loading`}>
          <Loader2 className="h-4 w-4 animate-spin" /> Loading document...
        </div>
      ) : fallback ? (
        <iframe title={policy?.title || "Policy document"} src={`${url}#toolbar=1&navpanes=0`} className={`w-full bg-white ${height}`} data-testid={`${testId}-frame`} />
      ) : (
        <div ref={containerRef} className={`overflow-auto bg-muted/40 p-3 space-y-3 ${height}`} data-testid={`${testId}-pages`}>
          {width > 0 && Array.from({ length: doc.numPages }, (_, i) => (
            <PdfPage key={`${i + 1}-${width}-${zoom}`} doc={doc} num={i + 1} width={width} zoom={zoom} onRendered={onPageRendered} testId={`${testId}-page-${i + 1}`} />
          ))}
        </div>
      )}
    </div>
  );
}

/** One page drawn on a canvas, scaled to `width` × `zoom` and crisp on high-DPI screens. */
function PdfPage({ doc, num, width, zoom, onRendered, testId }) {
  const canvasRef = useRef(null);
  const [h, setH] = useState(null);
  useEffect(() => {
    let alive = true;
    let task = null;
    doc.getPage(num).then((page) => {
      if (!alive) return;
      const base = page.getViewport({ scale: 1 });
      const scale = (width / base.width) * zoom;
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      if (!canvas) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      setH(Math.floor(viewport.height));
      const ctx = canvas.getContext("2d");
      task = page.render({ canvasContext: ctx, viewport, transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined });
      task.promise.then(() => { if (alive) onRendered?.(num); }).catch(() => { /* cancelled */ });
    }).catch(() => { if (alive) onRendered?.(num); });   // a broken page must not block the read-through check
    return () => { alive = false; try { task?.cancel(); } catch { /* ignore */ } };
  }, [doc, num, width, zoom, onRendered]);
  return (
    <div className="mx-auto w-fit max-w-full" data-testid={testId} data-pdf-page={num}>
      <canvas ref={canvasRef} className="block bg-white shadow-sm rounded-sm max-w-full" style={h ? undefined : { width: Math.floor(width * zoom), height: Math.floor(width * zoom * 1.3) }} aria-label={`Page ${num}`} />
      <p className="text-[10px] text-muted-foreground text-center mt-1">Page {num}</p>
    </div>
  );
}
